<template>
  <v-card elevation="3" rounded="xl" class="h-[600px] d-flex flex-column overflow-hidden">
    <!-- Header -->
    <v-card-title class="py-3 d-flex align-center justify-space-between">
      <span class="text-subtitle-1 font-weight-medium">Chat</span>
      <v-chip size="small" color="primary" variant="flat">
        {{ otherUserId ? 'Connected' : 'Waiting' }}
      </v-chip>
    </v-card-title>
    <v-divider />

    <!-- Messages -->
    <v-card-text class="flex-grow-1 pa-0">
      <div ref="messagesContainer" class="pa-4 overflow-y-auto h-100">
        <div v-for="msg in messages" :key="msg.id" class="mb-3">
          <!-- System line -->
          <template v-if="(msg.kind || 'user') === 'system'">
            <div class="d-flex align-center justify-center my-2 text-center">
              <v-divider class="mr-3" />
              <div
                class="px-3 py-1 rounded text-caption text-medium-emphasis bg-grey-lighten-4 d-inline-flex align-center">
                <v-icon size="14" class="mr-1">mdi-information-outline</v-icon>
                <span>{{ msg.message }}</span>
                <span class="ml-2">• {{ formatTime(msg.sent_at) }}</span>
              </div>
              <v-divider class="ml-3" />
            </div>
          </template>

          <!-- Regular bubble -->
          <template v-else>
            <div class="d-flex" :class="msg.sender_id === userId ? 'justify-end' : 'justify-start'">
              <div class="px-3 py-2 rounded-lg max-w-[70%]"
                :class="msg.sender_id === userId ? 'bg-primary text-white rounded-be-0' : 'bg-grey-lighten-4 rounded-bs-0'">
                <div class="text-body-2">{{ msg.message }}</div>
                <div class="text-caption mt-1 d-flex align-center"
                  :class="msg.sender_id === userId ? 'opacity-90' : 'text-medium-emphasis'">
                  {{ formatTime(msg.sent_at) }}
                  <span v-if="msg.sender_id === userId" class="ml-1">• {{ msg.read_at ? 'Read' : 'Sent' }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div v-if="messages.length === 0" class="text-center text-medium-emphasis py-8">
          No messages yet.
        </div>
      </div>
    </v-card-text>

    <v-divider />

    <!-- Input -->
    <v-card-actions class="pa-3">
      <v-textarea v-model="newMessage" variant="outlined" rounded="lg" :disabled="viewOnly"
        :placeholder="viewOnly ? 'View only' : 'Type a message…'" auto-grow rows="1" max-rows="4"
        class="flex-grow-1 mr-3" @keydown.enter.exact.prevent="sendMessage" @keydown.enter.shift.stop />
      <v-btn color="secondary" :disabled="!canSend" @click="sendMessage">Send</v-btn>
    </v-card-actions>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" location="bottom right" timeout="2500">
      {{ snackbar.msg }}
    </v-snackbar>
  </v-card>
</template>

<script>
import { supabase } from '@/supabaseClient'

export default {
  name: 'ChatRoom',
  props: {
    userId: { type: String, required: true },
    otherUserId: { type: String, required: true },
    listingID: { type: String, default: null },         // only used in mode 'listing'
    viewOnly: { type: Boolean, default: false },
    // NEW: 'auto' (default) picks 'listing' if listingID exists, otherwise 'dm'
    mode: { type: String, default: 'auto', validator: v => ['auto', 'listing', 'dm'].includes(v) }
  },
  data() {
    return {
      roomId: null,
      messages: [],
      newMessage: '',
      channel: null,
      snackbar: { show: false, color: 'error', msg: '' }
    }
  },
  computed: {
    canSend() {
      return !!this.newMessage && !!this.userId && !!this.otherUserId && !this.viewOnly
    },
    // resolved scope used by helpers
    resolvedMode() {
      return this.mode === 'auto' ? (this.listingID ? 'listing' : 'dm') : this.mode
    }
  },
  async created() {
    await this.resetAndLoad()
  },
  watch: {
    // If any of these change (navigate to a new chat, etc.), rebuild room and resubscribe
    userId() { this.resetAndLoad() },
    otherUserId() { this.resetAndLoad() },
    listingID() { this.resetAndLoad() },
    mode() { this.resetAndLoad() },
  },
  beforeUnmount() {
    if (this.channel) supabase.removeChannel(this.channel)
  },
  methods: {
    // Build a deterministic room id that encodes the scope
    buildRoomId() {
      const ids = [this.userId, this.otherUserId].filter(Boolean).sort().join('_')
      if (this.resolvedMode === 'listing') {
        return ['listing', this.listingID || 'no-listing', ids].join('__')
      }
      // direct message
      return ['dm', ids].join('__')
    },

    async resetAndLoad() {
      // tear down old channel
      if (this.channel) {
        supabase.removeChannel(this.channel)
        this.channel = null
      }
      this.roomId = this.buildRoomId()
      await this.loadMessages()
      this.subscribe()
    },

    async loadMessages() {
      const { data, error } = await supabase
        .from('chats')
        .select('*')
        .eq('room_id', this.roomId)
        .order('sent_at', { ascending: true })

      if (error) {
        console.error(error)
        this.toast('Failed to load messages.')
        return
      }
      this.messages = data || []
      this.scrollToBottom()
    },

    subscribe() {
      this.channel = supabase
        .channel(`chat-room:${this.roomId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'chats', filter: `room_id=eq.${this.roomId}` },
          payload => {
            const row = payload.new
            if (!row) return

            const exists = this.messages.some(m => m.id === row.id)
            if (!exists) {
              this.messages.push(row)
              this.scrollToBottom()
            }

            // Auto-mark as read only for user messages sent to me (2-party)
            if ((row.kind || 'user') === 'user' && row.receiver_id === this.userId && !row.read_at) {
              this.markRead(row.id)
            }
          }
        )
        .subscribe(status => {
          if (status !== 'SUBSCRIBED') console.error('Subscription status:', status)
        })
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const el = this.$refs.messagesContainer
        if (el) el.scrollTop = el.scrollHeight
      })
    },
    formatTime(ts) {
      if (!ts) return ''
      return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    async markRead(chatId) {
      const { error } = await supabase.from('chats').update({ read_at: new Date().toISOString() }).eq('id', chatId)
      if (error) console.error('markRead failed:', error)
    },
    async sendMessage() {
      if (!this.canSend) return
      const isListing = this.resolvedMode === 'listing'

      const messageObj = {
        kind: 'user',
        room_id: this.roomId,
        sender_id: this.userId,
        receiver_id: this.otherUserId,             // still 1:1
        listing_id: isListing ? this.listingID : null,
        message: this.newMessage,
        sent_at: new Date().toISOString(),
        read_at: null,
        meta: { scope: this.resolvedMode }         // optional: record scope
      }

      const { error } = await supabase.from('chats').insert([messageObj])
      if (error) {
        console.error('Failed to send message:', error)
        this.toast('Failed to send message.')
        return
      }
      this.newMessage = '' // realtime appends
    },

    async sendSystem(text, meta = null) {
      const isListing = this.resolvedMode === 'listing'
      const systemRow = {
        kind: 'system',
        room_id: this.roomId,
        sender_id: this.userId,
        receiver_id: this.otherUserId,
        listing_id: isListing ? this.listingID : null,
        message: text,
        sent_at: new Date().toISOString(),
        read_at: null,
        meta: { scope: this.resolvedMode, ...(meta || {}) }
      }
      const { error } = await supabase.from('chats').insert([systemRow])
      if (error) {
        console.error('Failed to add system message:', error)
        this.toast('Failed to add system message.')
      }
    },

    toast(msg, color = 'error') {
      this.snackbar = { show: true, color, msg }
    }
  }
}
</script>

<style scoped>
/* quick utility sizes to avoid adding Tailwind; remove if you use Tailwind */
.h-\[600px\] {
  height: 600px;
}

.max-w-\[70\%\] {
  max-width: 70%;
}

.rounded-be-0 {
  border-end-end-radius: 0 !important;
}

.rounded-bs-0 {
  border-end-start-radius: 0 !important;
}
</style>
